import { ImmagineAtlante, ImmagineAtlanteFormType, Tavola } from "../../types"
import {
  useImmagineAtlanteCreate,
  useImmagineAtlantePartialUpdate,
  useImmaginiAtlante,
  useImmagineAtlanteDelete,
  useUpdateImmaginiAtlantePositions,
} from "../../hooks/immaginIAtlante"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { DndContext, DragOverlay, useDraggable, useDroppable } from "@dnd-kit/core"
import { useCallback, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { Button, H5 } from "@inmagik/magik-ui"
import ImmagineAtlanteModal from "./ImmagineAtlanteModal"
import ModalDelete from "../../components/ModalDelete"

type TavolaMappaImmaginiProps = {
  tavola: Tavola
}

function DraggableImage({
  immagine,
  className,
  suffix,
  duplicate = false,
}: {
  immagine: ImmagineAtlante
  className?: string
  suffix?: string
  duplicate?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: suffix ? `${immagine.id.toString()}-${suffix}` : immagine.id.toString(),
  })
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : {}

  return (
    <>
      <div
        className={className}
        ref={setNodeRef}
        style={{
          ...style,
          zIndex: 500,
          opacity: isDragging ? 0 : 1,
          position: "absolute",
          width: "100%",
        }}
        {...listeners}
        {...attributes}
      >
        {!isDragging && (
          <img
            style={{ objectFit: "contain" }}
            src={immagine?.immagine}
            alt="Immagine Atlante"
            className="h-100 w-100"
          />
        )}
      </div>
      {duplicate && (
        <div className={className} style={{ top: 0, zIndex: 100, position: "absolute", width: "100%" }}>
          <img
            style={{ objectFit: "contain" }}
            src={immagine?.immagine}
            alt="Immagine Atlante"
            className="h-100 w-100"
          />
        </div>
      )}
    </>
  )
}

type DroppableCellProps = {
  cellId: string
  gridState: { [key: string]: string }
  immaginiAtlante: ImmagineAtlante[]
}

function DroppableCell({ cellId, gridState, immaginiAtlante }: DroppableCellProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: cellId,
  })
  const style = {
    backgroundColor: isOver ? "var(--bs-info)" : undefined,
  }
  const imageId = gridState[cellId] ? gridState[cellId].split("-")[0] : null

  const image = imageId ? immaginiAtlante.find((img) => img.id.toString() === imageId) : null

  return (
    <div className="tavolaCell" ref={setNodeRef} style={style}>
      {image && (
        <div className="h-100 w-100 position-absolute top-0 start-0">
          <DraggableImage
            className="h-100 w-100 bg-dark position-absolute top-0 start-0"
            immagine={image}
            suffix="grid"
          />
        </div>
      )}
    </div>
  )
}

type MappaImmaginiProps = {
  tavola: Tavola
  gridState: { [key: string]: string }
  immaginiAtlante: ImmagineAtlante[]
}

function MappaImmagini({ tavola, gridState, immaginiAtlante }: MappaImmaginiProps) {
  const tavolaContainerClass = tavola.template_griglia === "Verticale" ? "tavolaContainer-vertical" : "tavolaContainer"
  const tavolaRow2Class = tavola.template_griglia === "Verticale" ? "tavolaRow2-vertical" : "tavolaRow2"

  return (
    <div className={tavolaContainerClass}>
      <div className="tavolaRow1">
        <DroppableCell cellId="1" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <DroppableCell cellId="2" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <DroppableCell cellId="3" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <DroppableCell cellId="4" gridState={gridState} immaginiAtlante={immaginiAtlante} />
      </div>
      <div className={tavolaRow2Class}>
        <DroppableCell cellId="5" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <div className="tavolaCell" style={{ border: "solid var(--bs-primary) 2px" }}>
          {tavola?.immagine_principale_data && (
            <div className="h-100 w-100 position-absolute top-0 left-0">
              <img
                src={tavola.immagine_principale_data?.immagine}
                alt="Tavola"
                className="tavolaImage"
                draggable={false}
              />
            </div>
          )}
        </div>

        <DroppableCell cellId="6" gridState={gridState} immaginiAtlante={immaginiAtlante} />
      </div>
      <div className="tavolaRow1">
        <DroppableCell cellId="7" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <DroppableCell cellId="8" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <DroppableCell cellId="9" gridState={gridState} immaginiAtlante={immaginiAtlante} />
        <DroppableCell cellId="10" gridState={gridState} immaginiAtlante={immaginiAtlante} />
      </div>
    </div>
  )
}

function ImmagineAtlanteCard({
  immagine,
  associated,
  isActive,
  editDetail,
  deleteDetail,
  removeImage,
}: {
  immagine: ImmagineAtlante
  associated: boolean
  isActive: boolean
  editDetail: (dettaglio: ImmagineAtlante) => void
  deleteDetail: (dettaglio: ImmagineAtlante) => void
  removeImage: () => void
}) {
  return (
    <div className="card mb-2" style={{ backgroundColor: isActive ? " var(--bs-primary-bg-subtle)" : undefined }}>
      <div className="card-body">
        <div className="d-flex flex-row align-items-start">
          <div className="flex-shrink-0 w-25 position-relative" style={{ height: 150 }}>
            <DraggableImage duplicate immagine={immagine} className="p-2 border rounded bg-dark h-100" />
          </div>

          <div className="flex-grow-1 p-2 d-flex flex-column">
            <h6 className="card-title d-flex align-items-center gap-1">
              <div className={`d-flex flex-row align-items-center ${associated ? "text-success" : "text-danger"}`}>
                <i
                  className="material-symbols-rounded"
                  style={{
                    fontSize: 16,
                    fontVariationSettings: '"FILL" 1, "wght" 400, "GRAD" 0, "opsz" 48',
                  }}
                >
                  circle
                </i>
              </div>
              {immagine.titolo}
            </h6>
            <div style={{ maxHeight: 80 }} className="flex-grow-1 overflow-hidden ">
              <div
                className="card-text ellipsis flex-grow-1"
                dangerouslySetInnerHTML={{ __html: immagine.descrizione || "" }}
              ></div>
            </div>
            <div className="d-flex flex-row gap-2 mt-2 flex-wrap">
              {immagine.relazioni_data &&
                immagine.relazioni_data.map((relazione) => (
                  <div key={relazione.id} className="badge text-bg-primary text-white">
                    {relazione.nome}
                  </div>
                ))}
            </div>
          </div>

          <div
            className="d-flex flex-row align-items-center gap-1"
            // style={{ opacity: detail === dettaglio.id ? 1 : 0 }}
          >
            {associated && <Button leadingIcon="close" variant="dark" onClick={() => removeImage()} />}
            <Button
              leadingIcon="edit"
              variant="light"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                editDetail(immagine)
              }}
            />
            <Button
              leadingIcon="delete"
              variant="light"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                deleteDetail(immagine)
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function TavolaMappaImmagini({ tavola }: TavolaMappaImmaginiProps) {
  const { data: immaginiAtlante, refetch: refetchImmagini } = useImmaginiAtlante({
    tavola: tavola?.id.toString() || "",
  })

  const createImmagineAtlante = useImmagineAtlanteCreate()
  const updateImmagineAtlante = useImmagineAtlantePartialUpdate()
  const deleteImmagineAtlante = useImmagineAtlanteDelete()
  const [modalDeleteImmagineAtlanteState, modalDeleteImmagineAtlanteActions] = useModalTrigger()

  const [modalImmagineAtlanteCreateState, modalImmagineAtlanteCreateActions] = useModalTrigger()
  const [modalImmagineAtlanteUpdateState, modalImmagineAtlanteUpdateActions] = useModalTrigger()

  const updateImmaginiAtlantePositionsAction = useUpdateImmaginiAtlantePositions()

  const [gridState, setGridState] = useState<{ [key: string]: string }>({})
  const [activeId, setActiveId] = useState<string | null>(null)
  useEffect(() => {
    if (immaginiAtlante) {
      const initialGridState: { [key: string]: string } = {}
      immaginiAtlante.results.forEach((img) => {
        if (img.ordine) {
          initialGridState[img.ordine.toString()] = img.id.toString()
        }
      })
      setGridState(initialGridState)
    }
  }, [immaginiAtlante])

  const associatedImages = useMemo(() => {
    return Object.values(gridState).map((id) => Number(id))
  }, [gridState])

  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event

      if (over && active) {
        if (gridState[over.id] === active.id) {
          return // No change needed
        }
        const cleanActiveId = active.id.split("-")[0]
        const nextState = { ...gridState }
        //find image in other slots
        Object.entries(gridState).forEach(([cellId, imgId]) => {
          const cleanImageId = imgId.split("-")[0]
          if (cleanImageId === cleanActiveId) {
            //remove from previous slot
            delete nextState[cellId]
          }
        })

        const finalState = {
          ...nextState,
          [over.id]: cleanActiveId,
        }

        setGridState(finalState)
        const payload: any = Object.entries(finalState).map(([cellId, imgId]) => ({
          id: Number(imgId),
          ordine: Number(cellId),
        }))
        updateImmaginiAtlantePositionsAction
          .mutateAsync(payload)
          .catch((error) => {
            console.error("Error updating positions:", error)
          })
      }
      setActiveId(null)
    },
    [gridState, updateImmaginiAtlantePositionsAction],
  )

  const handleDragStart = useCallback((event: any) => {
    if (event?.active?.id) {
      setActiveId(event.active.id.toString())
    }
  }, [])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  const activeImage = useMemo(() => {
    if (!activeId) {
      return null
    }
    const cleanId = Number(activeId.split("-")[0])
    return immaginiAtlante?.results?.find((img) => img.id === cleanId) || null
  }, [activeId, immaginiAtlante])

  const [domReady, setDomReady] = useState(false)

  useEffect(() => {
    setDomReady(true)
  }, [])

  const portalElement = document.getElementById("buttons-portal")

  const removeImage = useCallback(
    (id: number) => {
      const nextState = { ...gridState }
      //find image in other slots
      Object.entries(gridState).forEach(([cellId, imgId]) => {
        const cleanImageId = imgId.split("-")[0]
        if (Number(cleanImageId) === Number(id)) {
          //remove from previous slot
          delete nextState[cellId]
        }
      })

      setGridState(nextState)
    },
    [gridState],
  )

  return (
    <>
      {domReady &&
        createPortal(
          <Button
            size="md"
            title="Aggiungi immagine"
            className="text-white"
            leadingIcon="add"
            onClick={() => {
              modalImmagineAtlanteCreateActions.open()
            }}
          />,
          portalElement!,
        )}

      <div className="h-100 w-100 overflow-hidden">
        <div className="row h-100">
          <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
            <div className="col-8 h-100">
              {tavola && (
                <MappaImmagini tavola={tavola} gridState={gridState} immaginiAtlante={immaginiAtlante?.results || []} />
              )}
            </div>
            <div className="col-4 h-100 overflow-auto">
              <div className="d-flex flex-column align-items-start mb-2 bg-primary p-2 rounded">
                <div className="bg-primary text-white">Immagini atlante</div>
              </div>
              <div className="px-2">
                <p className="smaller-text">Trascina le immagini sulla griglia per definire la posizione</p>
              </div>
              {immaginiAtlante?.results?.map((immagine) => (
                <ImmagineAtlanteCard
                  removeImage={() => removeImage(immagine.id)}
                  key={immagine.id}
                  immagine={immagine}
                  isActive={activeImage?.id === immagine.id}
                  associated={associatedImages.includes(immagine.id)}
                  editDetail={modalImmagineAtlanteUpdateActions.open}
                  deleteDetail={modalDeleteImmagineAtlanteActions.open}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={null} style={{ zIndex: 800 }}>
              {activeImage ? (
                <div className="tavolaCell position-relative" style={{ width: "150px", height: "150px" }}>
                  <img
                    style={{ objectFit: "contain" }}
                    src={activeImage.immagine}
                    alt="Immagine Atlante"
                    className="h-100 w-100"
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {modalImmagineAtlanteCreateState.isOpen && (
        <ImmagineAtlanteModal
          show={modalImmagineAtlanteCreateState.isOpen}
          onClose={modalImmagineAtlanteCreateActions.close}
          save={(values) => {
            return createImmagineAtlante
              .mutateAsync({
                ...values,
                tavola: Number(tavola.id),
              })
              .then(() => {
                modalImmagineAtlanteCreateActions.close()
              })
          }}
          title="Aggiungi immagine atlante"
        />
      )}
      {modalImmagineAtlanteUpdateState.isOpen && (
        <ImmagineAtlanteModal
          immagine={modalImmagineAtlanteUpdateState.value}
          show={modalImmagineAtlanteUpdateState.isOpen}
          onClose={modalImmagineAtlanteUpdateActions.close}
          save={(values) => {
            const payload = { ...values }
            if (typeof payload?.immagine === "string") {
              delete payload.immagine
            }
            return updateImmagineAtlante
              .mutateAsync({
                ...(payload as ImmagineAtlanteFormType),
              })
              .then(() => {
                modalImmagineAtlanteUpdateActions.close()
              })
          }}
          title="Modifica immagine atlante"
        />
      )}
      {modalDeleteImmagineAtlanteState.isOpen && (
        <ModalDelete
          isOpen={modalDeleteImmagineAtlanteState.isOpen}
          onClose={modalDeleteImmagineAtlanteActions.close}
          onDelete={() => {
            return deleteImmagineAtlante.mutateAsync(modalDeleteImmagineAtlanteState.value.id).then(() => {
              modalDeleteImmagineAtlanteActions.close()
              refetchImmagini()
            })
          }}
          message="Sei sicuro di voler eliminare l'immagine dell'atlante?"
          title="Elimina immagine atlante"
        />
      )}
    </>
  )
}
